import React from 'react';
import Box from '@mui/material/Box';
import { ServiceCategory } from '@prisma/client';
import { Grid2 } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Error from '../components/global/Error';
import MyDateRangePicker from '../components/filtering/DateRangePicker';
import SingleSelectFilter from '../components/filtering/SingleSelectFilter';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import ColoredAlert from '../components/global/ColoredAlert';
import useReports from '../hooks/reports/useReports';
import useTransformReports, {
  AppointmentTableData,
  OrderTableData,
} from '../hooks/reports/useTransformReports';
import VirtualizedTable from '../components/table/VirtualizedTable';
import {
  APPT_TABLE_COLUMNS,
  ORDER_TABLE_COLUMNS,
  RETAIL_CHECKOUTS_TABLE_DESCRIPTION,
  TABLE_HEADER_CONTENT,
  WARNING_CONTENT,
} from '../constants/reports';
import { DateType, TransactionType } from '../types/reports';
import PageLayout from '../components/global/PageLayout';
import RevenueReport from '../components/reports/RevenueReport';
import ReportBox from '../components/reports/ReportBox';
import CustomPieChart from '../components/reports/PieChart';
import { formatDollarAmount } from '../utils/global/helper';
import EmptyReport from '../components/reports/EmptyReport';
import AppointmentSideBarModal from '../components/appointments/AppointmentSideBarModal';
import { invalidateAppointment } from '../services/appointments';
import PaymentsSideBarModal from '../components/payments/PaymentsSideBarModal';
import { invalidatePaymentPaymentsGroup } from '../services/payments';
import PageLoading from '../components/global/PageLoading';

export default function ReportsPage() {
  const [selectedAppointmentId, setSelectedAppointmentId] = React.useState<
    string | null
  >(null);
  const [selectedPaymentId, setSelectedPaymentId] = React.useState<
    string | null
  >(null);

  const {
    query,
    state: {
      servicesFilter,
      dateTypeFilter,
      artistFilter,
      tripFilter,
      transactionTypeFilter,
      productsFilter,
      inactiveEmployeesFilter,
      ...state
    },
  } = useReports();
  const {
    data: {
      cashSales,
      creditSales,
      giftCardSales,
      totalSales,
      appointmentsTableData,
      ordersTableData,
      totalTips,
      salesTax,
    },
    downloadCsv,
  } = useTransformReports({
    dateType: dateTypeFilter.value,
    appointments: query.data?.appointments ?? [],
    orders: query.data?.orders ?? [],
    startDate: state.reportRange[0],
    endDate: state.reportRange[1],
    selectedProducts: productsFilter.value,
  });

  const onAppointmentClick = React.useCallback(
    (appointment: AppointmentTableData[number]) => {
      if (!appointment?.appointmentId) return;
      const appointmentId = appointment.appointmentId as string;
      invalidateAppointment(appointmentId);
      setSelectedAppointmentId(appointmentId);
    },
    [],
  );

  const onOrderClick = React.useCallback((order: OrderTableData[number]) => {
    if (!order?.paymentId) return;
    const paymentId = order.paymentId as string;
    invalidatePaymentPaymentsGroup(paymentId);
    setSelectedPaymentId(paymentId);
  }, []);

  if (query.isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage="Error fetching report data" />
      </Box>
    );
  }

  return (
    <>
      <PageLayout title="Reports" isLoading={query.isRefetching}>
        <div className="calendar-header reports-header">
          <Box
            display="flex"
            gap={1}
            className="header-left"
            component="div"
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
            }}>
            <div className="joined-pickers">
              <MyDateRangePicker
                storageKey="ReportsDateRange"
                value={state.reportRange}
                setValue={state.setReportRange}
              />
              <SingleSelectFilter
                name={dateTypeFilter.name}
                options={dateTypeFilter.options}
                state={dateTypeFilter.value}
                setState={newState =>
                  dateTypeFilter.setValue(newState as DateType)
                }
                styleOverride={{ minWidth: '250px' }}
              />
            </div>
            {state.showArtistFilter && (
              <MultiSelectFilter
                emptyLabel="No artists"
                name={artistFilter.name}
                options={artistFilter.options}
                state={artistFilter.value}
                // sorting prevents refetching when the order of selected employees changes
                setState={newSelected => {
                  const arrValue = Array.isArray(newSelected)
                    ? newSelected
                    : newSelected(artistFilter.value);
                  artistFilter.setValue(arrValue.sort());
                }}
              />
            )}
            <SingleSelectFilter
              name={servicesFilter.name}
              options={servicesFilter.options}
              state={servicesFilter.value ?? 'ALL'}
              styleOverride={{ width: 'auto' }}
              setState={newState => {
                servicesFilter.setValue(newState as ServiceCategory);
              }}
            />

            {state.showTripsFilter && (
              <MultiSelectFilter
                emptyLabel="All"
                name={tripFilter.name}
                options={tripFilter.options}
                state={tripFilter.value}
                // sorting prevents refetching when the order of selected employees changes
                setState={newSelected => {
                  const arrValue = Array.isArray(newSelected)
                    ? newSelected
                    : newSelected(tripFilter.value);
                  tripFilter.setValue(arrValue.sort());
                }}
              />
            )}
            {state.showOrdersOptions && (
              <SingleSelectFilter
                name={transactionTypeFilter.name}
                options={transactionTypeFilter.options}
                state={transactionTypeFilter.value ?? 'ALL'}
                styleOverride={{ width: 'auto' }}
                setState={newState =>
                  transactionTypeFilter.setValue(newState as TransactionType)
                }
              />
            )}
            {state.showProductsOptions && (
              <MultiSelectFilter
                emptyLabel="All products"
                name={productsFilter.name}
                options={productsFilter.options}
                state={productsFilter.value}
                setState={newSelected => {
                  const arrValue = Array.isArray(newSelected)
                    ? newSelected
                    : newSelected(productsFilter.value);
                  productsFilter.setValue(arrValue.sort());
                }}
              />
            )}
            {state.showInactiveEmployeesOption && (
              <SingleSelectFilter
                name={inactiveEmployeesFilter.name}
                options={inactiveEmployeesFilter.options}
                state={inactiveEmployeesFilter.value.toString()}
                setState={newState =>
                  inactiveEmployeesFilter.setValue(newState === 'true')
                }
                styleOverride={{ minWidth: '255px' }}
              />
            )}
          </Box>
        </div>
        <Box width="100%" display="flex" flexDirection="column" gap={3}>
          {query.isLoading && <PageLoading />}
          {!query.isLoading && (
            <>
              <ColoredAlert
                type="warning"
                title={WARNING_CONTENT[dateTypeFilter.value].title}
                subtitle={WARNING_CONTENT[dateTypeFilter.value].subtitle}
                animate={false}
              />

              <RevenueReport
                totalSales={totalSales}
                cashSales={cashSales}
                creditSales={creditSales}
                giftCardSales={giftCardSales}
                isRefetching={query.isRefetching}
                headerContent={{
                  title: 'Revenue Report',
                  subtitle:
                    'Based on the filters selected above, this report will show your total revenue broken out by credit, cash, and gift cards. Revenue report DOES NOT include tips and tax.',
                }}
              />

              <Grid2 container spacing={2}>
                <Grid2 size={{ sm: 12, md: 6 }}>
                  <ReportBox
                    headerContent={{
                      title: 'Tips Report',
                      subtitle:
                        'Based on the filters selected above, this report will show your staff’s tips broken out by credit, cash, and gift cards.',
                    }}>
                    {totalTips.total ? (
                      <CustomPieChart
                        data={[
                          { name: 'Cash', value: totalTips.cash },
                          { name: 'Credit', value: totalTips.credit },
                          { name: 'Gift Card', value: totalTips.giftCard },
                        ]}
                        dataKey="value"
                        xAxisKey="name"
                        type="dollar"
                        withTooltip
                        chartTotalValue={formatDollarAmount(totalTips.total)}
                      />
                    ) : (
                      <EmptyReport />
                    )}
                  </ReportBox>
                </Grid2>
                <Grid2 size={{ sm: 12, md: 6 }}>
                  <ReportBox
                    headerContent={{
                      title: 'Sales Tax Report',
                      subtitle:
                        'Based on the filters selected above, this report will show sales tax collected broken out by credit, cash, and gift cards.',
                    }}>
                    {salesTax.total ? (
                      <CustomPieChart
                        data={[
                          { name: 'Cash', value: salesTax.cash },
                          { name: 'Credit', value: salesTax.credit },
                          { name: 'Gift Card', value: salesTax.giftCard },
                        ]}
                        dataKey="value"
                        xAxisKey="name"
                        type="dollar"
                        withTooltip
                        chartTotalValue={formatDollarAmount(salesTax.total)}
                      />
                    ) : (
                      <EmptyReport />
                    )}
                  </ReportBox>
                </Grid2>
              </Grid2>

              <VirtualizedTable
                headerContent={{
                  title: 'Appointment Transactions Report',
                  subtitle: TABLE_HEADER_CONTENT[dateTypeFilter.value],
                  action: {
                    label: 'Download',
                    onClick: () => downloadCsv('appointment'),
                    icon: <FileDownloadOutlinedIcon />,
                    disabled: query.isFetching,
                  },
                }}
                columns={APPT_TABLE_COLUMNS}
                data={appointmentsTableData}
                height={480}
                isLoading={query.isRefetching}
                emptyText="No appointments found for selected date range."
                onRowClick={onAppointmentClick}
              />

              {state.showOrdersOptions && (
                <VirtualizedTable
                  headerContent={{
                    title: 'Retail Checkouts Report',
                    subtitle: RETAIL_CHECKOUTS_TABLE_DESCRIPTION,
                    action: {
                      label: 'Download',
                      onClick: () => downloadCsv('order'),
                      icon: <FileDownloadOutlinedIcon />,
                      disabled: query.isFetching,
                    },
                  }}
                  columns={ORDER_TABLE_COLUMNS}
                  data={ordersTableData}
                  height={480}
                  isLoading={query.isRefetching}
                  emptyText="No orders found for selected date range."
                  onRowClick={onOrderClick}
                />
              )}
            </>
          )}
        </Box>
      </PageLayout>
      {selectedAppointmentId && (
        <AppointmentSideBarModal
          isOpen
          appointmentId={selectedAppointmentId}
          onClose={() => setSelectedAppointmentId(null)}
        />
      )}
      {selectedPaymentId && (
        <PaymentsSideBarModal
          paymentId={selectedPaymentId}
          onClose={() => setSelectedPaymentId(null)}
        />
      )}
    </>
  );
}
